import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  OnDestroy,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Subscription, switchMap, throwError } from 'rxjs';
import { ScreeningContentItem } from '../../data/screening-results.model';
import { NoodleService } from '@/app/services/noodle/noodle.service';
import { failedReviewResultIds } from './override-decision.helpers';

/**
 * Records why a raised flag is being lowered, then tells Noodle the content is approved.
 *
 * The left column shows the post. The right column is the note and the commit action.
 */
@Component({
  selector: 'scr-override-decision-drawer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './override-decision-drawer.html',
  styleUrl: './override-decision-drawer.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class OverrideDecisionDrawer implements OnDestroy {
  private readonly noodle = inject(NoodleService);
  private readonly subscriptionList = new Subscription();
  private readonly overrideDialog = viewChild<ElementRef<HTMLDialogElement>>('overrideDialog');

  readonly content = input.required<ScreeningContentItem>();
  readonly briefId = input.required<string>();
  readonly overridden = output<string>();

  readonly note = signal('');
  readonly saveState = signal<'idle' | 'saving' | 'error'>('idle');

  readonly dialogId = computed(() => {
    const id = this.content().id || 'item';
    return `override-decision-${id.replace(/[^A-Za-z0-9_-]/g, '')}`;
  });

  readonly noteId = computed(() => `${this.dialogId()}-note`);

  readonly canCommit = computed(() => this.note().trim().length > 0 && this.saveState() !== 'saving');

  /** Opens a fresh note for this post. */
  open(): void {
    this.note.set('');
    this.saveState.set('idle');
    const dialog = this.overrideDialog()?.nativeElement;
    if (dialog && !dialog.open) {
      dialog.showModal();
    }
  }

  onNoteInput(event: Event): void {
    const target = event.target;
    if (!(target instanceof HTMLTextAreaElement)) {
      return;
    }
    this.note.set(target.value);
    if (this.saveState() === 'error') {
      this.saveState.set('idle');
    }
  }

  /**
   * Approves every open review row on this post. An empty note never leaves the drawer.
   * Noodle's override id is the review row, which only `get_results` carries.
   */
  commit(): void {
    const note = this.note().trim();
    const contentId = this.content().id;
    const briefId = this.briefId();
    if (!note || !contentId || !briefId || this.saveState() === 'saving') {
      return;
    }
    this.saveState.set('saving');
    this.subscriptionList.add(
      this.noodle.getScreeningResultsDirect(contentId, briefId).pipe(
        switchMap((results) => {
          const reviewResultIds = failedReviewResultIds(results);
          if (reviewResultIds.length === 0) {
            return throwError(() => new Error('No open review rows'));
          }
          return this.noodle.updateDecision(contentId, reviewResultIds, note);
        }),
      ).subscribe({
        next: () => {
          this.saveState.set('idle');
          this.closeDrawer();
          this.overridden.emit(contentId);
        },
        error: () => {
          this.saveState.set('error');
        },
      }),
    );
  }

  closeDrawer(): void {
    this.stopPlayingVideo();
    this.overrideDialog()?.nativeElement?.close();
  }

  /** Pause before the dialog closes so playback does not continue behind it. */
  private stopPlayingVideo(): void {
    const dialog = this.overrideDialog()?.nativeElement;
    dialog?.querySelectorAll('video').forEach((video) => {
      if (!video.paused) {
        video.pause();
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }
}
